/**
 * Owner: Masa Bayu
 * File: DedicatedSalesScreen.tsx
 * Date:Time: 2026-10-17 03:34
 * Description: Dedicated author-sales screen — SaleRecord table projected from confirmed
 *   Commerce Orders only. Purpose-built sales UI in the profile palette.
 */

import { useState } from 'react';
import {
  getDemoSaleRecords,
  getDemoSalesSummary,
  projectDemoAuthorSales,
  resetDemoAuthorSales,
} from '../demo-author-sales';
import { formatMinor } from './dedicated-format';
import './dedicated.css';

export interface DedicatedSalesScreenProps {
  readonly onNavigate: (path: string) => void;
}

export function DedicatedSalesScreen({ onNavigate }: DedicatedSalesScreenProps) {
  const [status, setStatus] = useState('Project sales from confirmed Commerce Orders.');
  const [records, setRecords] = useState(() => getDemoSaleRecords());
  const summary = getDemoSalesSummary();

  const refresh = (nextStatus: string) => {
    setStatus(nextStatus);
    setRecords(getDemoSaleRecords());
  };

  return (
    <div className="ddx">
      <header>
        <p className="ddx__eyebrow">Author · Commerce</p>
        <h2 className="ddx__title">Sales</h2>
        <p className="ddx__lede">
          SaleRecord projections from confirmed Orders only. The author UI never mints sales or
          payment authority — Stripe remains PSP under Commerce.
        </p>
      </header>

      {summary !== null ? (
        <div className="ddx__stats">
          <div className="ddx__stat ddx__stat--pink">
            <span className="ddx__stat-value">{String(summary.unitsSold)}</span>
            <span className="ddx__stat-label">Units sold</span>
          </div>
          <div className="ddx__stat ddx__stat--violet">
            <span className="ddx__stat-value">
              {formatMinor(summary.sellerSubtotalMinorUnits, summary.currency)}
            </span>
            <span className="ddx__stat-label">Seller subtotal</span>
          </div>
          <div className="ddx__stat ddx__stat--teal">
            <span className="ddx__stat-value">{String(summary.saleCount)}</span>
            <span className="ddx__stat-label">Sale records</span>
          </div>
        </div>
      ) : null}

      <p className="ddx__status" role="status" aria-live="polite">
        {status}
      </p>

      <div className="ddx__actions">
        <button
          type="button"
          className="ddx__cta ddx__cta--solid"
          onClick={() => {
            refresh(projectDemoAuthorSales());
          }}
        >
          Project from confirmed order
        </button>
        <button
          type="button"
          className="ddx__cta"
          onClick={() => {
            resetDemoAuthorSales();
            refresh('Sales cleared.');
          }}
        >
          Reset
        </button>
        <button
          type="button"
          className="ddx__cta"
          onClick={() => {
            onNavigate('/author');
          }}
        >
          Dashboard
        </button>
      </div>

      {records.length === 0 ? (
        <div className="ddx__empty">
          <h3>No sale records yet</h3>
          <p>Records appear here once a Commerce Order for your title is confirmed.</p>
        </div>
      ) : (
        <div className="ddx__table-wrap">
          <table className="ddx__table" aria-label="Author sale records">
            <thead>
              <tr>
                <th scope="col">Title</th>
                <th scope="col">Order</th>
                <th scope="col">Subtotal</th>
              </tr>
            </thead>
            <tbody>
              {records.map((record) => (
                <tr key={record.saleRecordId}>
                  <td>{record.title}</td>
                  <td>{record.orderId}</td>
                  <td>{formatMinor(record.sellerSubtotalMinorUnits, record.currency)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
